import { useEffect, useRef, ReactNode } from 'react'
import styled, { keyframes } from 'styled-components'
import { tokens } from '../../styles/tokens'
import { Button } from './Button'

interface Props {
  open: boolean
  title: string
  onClose: () => void
  onConfirm?: () => void
  confirmLabel?: string
  confirmLoading?: boolean
  confirmDisabled?: boolean
  children: ReactNode
  width?: string
}

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`
const slideUp = keyframes`from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); }`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(45, 27, 78, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 24px;
  animation: ${fadeIn} 0.15s ease;
`

const Panel = styled.div<{ $width: string }>`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: ${p => p.$width};
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.2s ease;
  box-shadow: 0 20px 60px rgba(45, 27, 78, 0.2);
`

const Header = styled.div`
  padding: 24px 28px 20px;
  border-bottom: 1px solid ${tokens.purple100};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`

const Title = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: 22px;
  font-weight: 400;
  color: ${tokens.purple900};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${tokens.purple200};
  font-size: 20px;
  line-height: 1;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.15s;
  &:hover {
    color: ${tokens.purple600};
  }
`

const Body = styled.div`
  padding: 24px 28px;
  overflow-y: auto;
  flex: 1;
`

const Footer = styled.div`
  padding: 16px 28px;
  border-top: 1px solid ${tokens.purple100};
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
`

export function Dialog({
  open,
  title,
  onClose,
  onConfirm,
  confirmLabel = 'Save',
  confirmLoading,
  confirmDisabled,
  children,
  width = '520px',
}: Props) {
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <Overlay
      ref={overlayRef}
      onClick={e => {
        if (e.target === overlayRef.current) onClose()
      }}
    >
      <Panel $width={width}>
        <Header>
          <Title>{title}</Title>
          <CloseButton onClick={onClose}>✕</CloseButton>
        </Header>
        <Body>{children}</Body>
        {onConfirm && (
          <Footer>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={onConfirm} loading={confirmLoading} disabled={confirmDisabled}>
              {confirmLabel}
            </Button>
          </Footer>
        )}
      </Panel>
    </Overlay>
  )
}
